// =============================================================================
// Optim TURPE V2 — la MARGE DE SÉCURITÉ (DEV-1913, bloquant B1, constat F2.2).
//
// Ce que le client doit vivre : le curseur qu'il déplace produit la marge qu'il
// a demandée. Rien de plus.
//
// Le défaut corrigé ici : l'écran envoyait la valeur du curseur telle quelle
// (`marge_securite_pct: 5`) alors que le moteur attend une **FRACTION entre 0 et
// 1** (`optimturpe_analyse_inputs_model.py`, contrat cible §3.3). Un 5 demandé
// arrivait donc comme 500 % de marge, et un 10 comme 1 000 %. Le nom du champ
// (`_pct`) est la source de la confusion ; il reste au contrat, la conversion
// vit ici, une seule fois, avec ses bornes.
//
// Une valeur hors borne est REFUSÉE À L'ÉCRAN avant l'envoi : mieux vaut ne pas
// partir que de faire chiffrer une marge que personne n'a voulue.
// =============================================================================

/**
 * Les crans offerts à l'écran, en points de pourcentage. Contrat de
 * l'interface v1.2 §8.2.6 (lot 4d) : quatre crans, 0 % compris, un choix
 * nommé et non plus un bord de curseur.
 */
export const MARGE_PRESETS_PCT = [0, 2, 5, 10] as const

/** Borne basse du curseur : aucune marge. */
export const MARGE_PCT_MIN = 0

/**
 * Borne haute du curseur, en points de pourcentage. Trente points au-dessus de
 * la puissance retenue est déjà très prudent ; au-delà, la recommandation cesse
 * d'être une recommandation. Le contrat du moteur, lui, borne la fraction à 1.
 */
export const MARGE_PCT_MAX = 30

/** Pas du curseur : un point de pourcentage, soit 31 positions de 0 à 30. */
export const MARGE_PCT_PAS = 1

/** Valeur par défaut du curseur, inchangée depuis DEV-783. */
export const MARGE_PCT_DEFAUT = 5

/** Borne haute de la FRACTION au contrat du moteur (§3.3). */
export const MARGE_FRACTION_MAX = 1

/** Les 31 positions atteignables par le curseur, dans l'ordre. */
export function positionsDuCurseur(): number[] {
    const positions: number[] = []
    for (let pct = MARGE_PCT_MIN; pct <= MARGE_PCT_MAX; pct += MARGE_PCT_PAS) {
        positions.push(pct)
    }
    return positions
}

/**
 * La valeur du curseur est-elle envoyable ?
 *
 * Refuse tout ce qui n'est pas un nombre fini (un champ vidé rend `NaN`, et
 * `NaN > 30` est faux : une garde ouverte laisserait passer l'absence), tout ce
 * qui sort des bornes de l'écran, et tout ce qui dépasserait la fraction 1 du
 * contrat du moteur.
 */
export function estMargeValide(pct: unknown): boolean {
    if (typeof pct !== "number" || !Number.isFinite(pct)) return false
    if (pct < MARGE_PCT_MIN || pct > MARGE_PCT_MAX) return false
    return pct / 100 <= MARGE_FRACTION_MAX
}

/**
 * Convertit la valeur du curseur en FRACTION pour le moteur, ou rend `null`
 * quand elle n'est pas envoyable.
 *
 * `null` est un refus, pas un 0 : l'appelant n'envoie alors pas le champ, et le
 * moteur garde son défaut. Envoyer 0 à la place serait affirmer « aucune marge »
 * sur une saisie qu'on n'a pas comprise.
 *
 * L'arrondi à quatre décimales efface le bruit binaire de la division (7 / 100
 * vaut 0,070000000000000007 en flottant) : ce que le client lit sur le curseur
 * et ce que le moteur reçoit désignent alors le même nombre.
 */
export function fractionDepuisPourcentage(pct: unknown): number | null {
    if (!estMargeValide(pct)) return null
    return Number(((pct as number) / 100).toFixed(4))
}

/** Ce que la relecture a compris de la valeur servie, et ce qu'elle n'a pas pu trancher. */
export type MargeRelue = {
    /** La position du curseur, en points de pourcentage. */
    pct: number
    /**
     * Comment `pct` a été obtenu : la valeur relue comme une `fraction` (le
     * contrat actuel), comme un `pourcentage` (écran antérieur), ou le `defaut`
     * du curseur faute de savoir.
     */
    lecture: "fraction" | "pourcentage" | "defaut"
    /**
     * Vrai quand la valeur servie se lit des DEUX façons et qu'elles ne donnent
     * pas la même marge. La page le dit alors sur le champ, au lieu de choisir
     * en silence.
     */
    ambigue: boolean
    /** L'autre lecture possible, quand elle existe. Sert à formuler l'ambiguïté. */
    autreLecturePct: number | null
    /** La valeur telle que le document la porte, pour la citer à l'écran. */
    valeurServie: number | null
}

/**
 * Relit une valeur servie par le document pour repositionner le curseur.
 *
 * Deux conventions se croisent dans les documents : le contrat actuel veut une
 * FRACTION entre 0 et 1, et une version ANTÉRIEURE de l'écran envoyait des
 * points de pourcentage. Au-delà de 1, il n'y a pas d'ambiguïté (ce ne peut pas
 * être une fraction). En DESSOUS de 1, non plus (un pourcentage inférieur à 1
 * point n'est pas une position du curseur, dont le pas vaut un point).
 *
 * Le cas limite est la valeur 1 : elle vaut 100 % lue en fraction, 1 % lue en
 * pourcentage, et les deux lectures sont légitimes. L'ancienne version tranchait
 * en silence vers la fraction, trouvait 100 % hors borne, et retombait sur le
 * défaut de 5 % sans le dire : le client voyait un curseur à 5 pour une étude
 * calculée avec une autre marge (revue C7). On rend désormais l'ambiguïté, et
 * la page l'écrit sur le champ.
 */
export function relireValeurServie(valeur: unknown): MargeRelue {
    if (typeof valeur !== "number" || !Number.isFinite(valeur)) {
        return {
            pct: MARGE_PCT_DEFAUT,
            lecture: "defaut",
            ambigue: false,
            autreLecturePct: null,
            valeurServie: null,
        }
    }

    const commeFraction = Math.round(valeur * 100)
    const commePourcentage = Math.round(valeur)
    const fractionValide =
        valeur <= MARGE_FRACTION_MAX && estMargeValide(commeFraction)
    // Une lecture « points de pourcentage » n'est plausible que sur un ENTIER :
    // le curseur avance d'un point, il n'a jamais écrit 0,05 %. Sans cette
    // condition, 0,05 (soit 5 %) passerait pour ambigu avec un 0 % imaginaire.
    const pourcentageValide =
        Number.isInteger(valeur) && estMargeValide(commePourcentage)

    // Les deux lectures tiennent et ne disent pas la même chose → on ne tranche
    // pas. On garde la lecture du contrat actuel quand elle est valide, sinon
    // l'autre, et l'appelant DIT que la valeur servie est ambiguë.
    const ambigue =
        fractionValide &&
        pourcentageValide &&
        commeFraction !== commePourcentage

    if (fractionValide) {
        return {
            pct: commeFraction,
            lecture: "fraction",
            ambigue,
            autreLecturePct: pourcentageValide ? commePourcentage : null,
            valeurServie: valeur,
        }
    }
    if (pourcentageValide) {
        return {
            pct: commePourcentage,
            lecture: "pourcentage",
            // Une fraction hors borne (1 → 100 %) n'est pas une marge tenable :
            // on prend le pourcentage, et on le dit.
            ambigue: valeur <= MARGE_FRACTION_MAX,
            autreLecturePct:
                valeur <= MARGE_FRACTION_MAX ? commeFraction : null,
            valeurServie: valeur,
        }
    }
    return {
        pct: MARGE_PCT_DEFAUT,
        lecture: "defaut",
        ambigue: false,
        autreLecturePct: null,
        valeurServie: valeur,
    }
}

/** La seule position du curseur, pour les appelants qui n'ont pas à parler de l'ambiguïté. */
export function pourcentageDepuisValeurServie(valeur: unknown): number {
    return relireValeurServie(valeur).pct
}

// =============================================================================
// La marge PAR DÉFAUT d'un lancement (retour de relecture du 07/10, DEV-2311).
//
// Ce que le client doit vivre : « De base on lance en équilibré pour avoir une
// petite marge de sécurité. » Tout lancement parti de la page (premier
// lancement, actualisation, rejeu) porte le réglage « Équilibré » du curseur,
// tant que l'utilisateur n'a pas choisi une autre prudence. Le moteur, lui, ne
// change pas : sans le champ, il calcule toujours sans marge.
// =============================================================================

/** Le cran « Standard » (ex-« Équilibré »), en points de pourcentage. */
export const MARGE_PCT_EQUILIBRE: number = MARGE_PCT_DEFAUT

/** La fraction envoyée au moteur quand l'utilisateur n'a rien choisi. */
export const MARGE_FRACTION_PAR_DEFAUT = Number(
    (MARGE_PCT_EQUILIBRE / 100).toFixed(4)
)

type OptimisationEnvoyee = { mode?: unknown; marge_securite_pct?: unknown }

function porteUneMarge(optimisation: OptimisationEnvoyee | undefined): boolean {
    const v = optimisation?.marge_securite_pct
    return typeof v === "number" && Number.isFinite(v)
}

/**
 * Le corps d'un LANCEMENT neuf, complété de la marge : celle que l'utilisateur
 * a choisie pour ce compteur pendant la visite (fraction), sinon « Équilibré ».
 * Une marge déjà portée par le corps (le bloc d'affinage l'envoie toujours)
 * n'est jamais remplacée ; un scénario saisi (`price_only`) n'en reçoit pas,
 * le moteur chiffre la structure telle quelle.
 */
export function corpsAvecMargeParDefaut<
    T extends { optimisation?: OptimisationEnvoyee },
>(corps: T, fractionChoisie?: number | null): T {
    if (corps.optimisation?.mode === "price_only") return corps
    if (porteUneMarge(corps.optimisation)) return corps
    return {
        ...corps,
        optimisation: {
            ...corps.optimisation,
            marge_securite_pct: fractionChoisie ?? MARGE_FRACTION_PAR_DEFAUT,
        },
    }
}

/**
 * Les surcharges d'un REJEU, complétées de la marge. Le rejeu reprend les
 * entrées de l'étude rejouée : une marge non nulle y est un choix de
 * l'utilisateur (la page n'en envoyait aucune avant ce retour) et se garde.
 * Une étude sans marge ou à 0 % reçoit le choix fait pendant la visite, sinon
 * « Équilibré ». Un scénario saisi n'en reçoit pas.
 */
export function surchargesAvecMargeParDefaut(
    surcharges: Record<string, unknown> | null | undefined,
    optimisationRejouee: OptimisationEnvoyee | null | undefined,
    fractionChoisie?: number | null
): Record<string, unknown> {
    const base = { ...(surcharges ?? {}) }
    const optimisation = base.optimisation as OptimisationEnvoyee | undefined
    if (porteUneMarge(optimisation)) return base
    if (optimisationRejouee?.mode === "price_only") return base
    let fraction: number | null = fractionChoisie ?? null
    if (fraction === null) {
        const servie = optimisationRejouee?.marge_securite_pct
        if (typeof servie === "number" && Number.isFinite(servie) && servie > 0)
            return base
        fraction = MARGE_FRACTION_PAR_DEFAUT
    }
    base.optimisation = { ...optimisation, marge_securite_pct: fraction }
    return base
}

/**
 * Les crans de la marge de sécurité, dans l'ordre des présets : « Aucune
 * (0 %) · Minimale (2 %) · Standard (5 %) · Confortable (10 %) » (contrat
 * v1.2 §8.2.6, lot 4d ; « Minimale » est le mot du lexique SSOT, L2 de
 * Mélodie tranché le 15/10/2026).
 */
export const NOM_DES_CRANS_DE_MARGE: Record<number, string> = {
    0: "Aucune",
    2: "Minimale",
    5: "Standard",
    10: "Confortable",
}

/** « Standard (5 %) » : le libellé d'un cran du formulaire, `null` hors cran. */
export function libelleCranDeMarge(pct: number): string | null {
    const nom = NOM_DES_CRANS_DE_MARGE[pct]
    return nom ? `${nom} (${pct} %)` : null
}

/**
 * La marge de sécurité telle que la ligne des hypothèses l'écrit (Q9) :
 * « aucune (0 %) », « standard (5 %) » et les deux autres crans par leur nom,
 * sinon le pourcentage seul.
 */
export function libelleMarge(pct: number): string {
    if (pct === 0) return "aucune (0 %)"
    const nom = NOM_DES_CRANS_DE_MARGE[pct]
    return nom ? `${nom.toLowerCase()} (${pct} %)` : `${pct} %`
}

/**
 * La marge d'un C5 AVANT le calcul (Q10) : le moteur applique d'office son
 * défaut interne (courtage `find_best_fta_puissance_C5.py`,
 * `C5_MARGE_INTERNE_DEFAUT = 0.15`, vérifié au moteur v182) et ignore celle que
 * l'écran envoie ; le curseur est masqué et la valeur se dit telle quelle.
 * Ce texte ne vaut QUE pour le formulaire de lancement : une fois l'étude
 * calculée, c'est la marge servie qui se lit (`libelleMargeC5Servie`).
 */
export const MARGE_PCT_C5 = 15
export const LIBELLE_MARGE_C5 = `${MARGE_PCT_C5} %, appliquée d'office`

/**
 * La marge d'un C5 APRÈS le calcul : celle que le moteur a réellement
 * appliquée, servie sous `restitution.pmax_mesuree.marge_appliquee` (fraction).
 * Si le moteur change sa marge C5, la page dit le nouveau chiffre, jamais la
 * constante de l'écran. `null` quand elle n'est pas servie (étude antérieure,
 * étude saisie) : la ligne se tait plutôt que d'affirmer un 15 % non prouvé.
 */
export function libelleMargeC5Servie(
    pmaxMesuree: { marge_appliquee?: number | null } | null | undefined
): string | null {
    const servie = pmaxMesuree?.marge_appliquee
    if (typeof servie !== "number" || !Number.isFinite(servie) || servie < 0)
        return null
    const pct = Math.round(servie * 1000) / 10
    if (pct === 0) return libelleMarge(0)
    return `${String(pct).replace(".", ",")} %, appliquée d'office`
}
